import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastKind = 'success' | 'error' | 'info'

interface Toast {
  id: number
  message: string
  kind: ToastKind
}

interface ToastContextValue {
  push: (message: string, kind?: ToastKind) => void
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue>({ push: () => {} })

let _counter = 0

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id))
    timers.current.delete(id)
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const push = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = ++_counter
    setToasts(prev => [...prev.slice(-4), { id, message, kind }]) // max 5 at once
    const timer = setTimeout(() => dismiss(id), 4000)
    timers.current.set(id, timer)
  }, [dismiss])

  // clean up timers on unmount
  useEffect(() => {
    return () => { timers.current.forEach(t => clearTimeout(t)) }
  }, [])

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast() {
  return useContext(ToastContext)
}

// ─── Container ────────────────────────────────────────────────────────────────

const KIND_STYLES: Record<ToastKind, { bg: string; border: string; icon: string; iconColor: string }> = {
  success: { bg: '#F0FDF4', border: '#BBF7D0', icon: '✓',  iconColor: '#16A34A' },
  error:   { bg: '#FEF2F2', border: '#FECACA', icon: '✕',  iconColor: '#DC2626' },
  info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ',  iconColor: '#1D4ED8' },
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed', bottom: 24, right: 24,
        display: 'flex', flexDirection: 'column', gap: 10,
        zIndex: 9999, maxWidth: 380,
        pointerEvents: 'none',
      }}
    >
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [visible, setVisible] = useState(false)
  const s = KIND_STYLES[toast.kind]

  useEffect(() => {
    // mount → fade-in on next frame
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])

  return (
    <div
      role="alert"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: s.bg, border: `1px solid ${s.border}`,
        borderRadius: 10, padding: '11px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        pointerEvents: 'all', cursor: 'default',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 180ms ease-out, transform 180ms ease-out',
        maxWidth: 380, minWidth: 240,
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 700, color: s.iconColor, flexShrink: 0, lineHeight: '20px' }}>
        {s.icon}
      </span>
      <span style={{ fontSize: 13, color: '#111827', lineHeight: 1.5, flex: 1, fontFamily: 'var(--font-sans)' }}>
        {toast.message}
      </span>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#9CA3AF', padding: '0 2px', fontSize: 14, lineHeight: '20px',
          flexShrink: 0,
        }}
      >×</button>
    </div>
  )
}
